"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import Label from "./ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Upload, X, Trash2, Check, CheckCircle2, MapPin, RefreshCw, ChevronDown, User, Building2, ShieldCheck, Lock, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import SingleSelectDropdown from "./ui/singleselect-dropdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch"; // ✅ use shadcn switch
import { toast } from "react-hot-toast";

import {
  getMyProfile,
  getPracticeNames,
  updateProfile as updateProfileApi,
  uploadAvatar as uploadAvatarApi,
  enableMfa as enableMfaApi,   // expects enableMfa(true/false)
  setupTotp as setupTotpApi,
  verifyTotp as verifyTotpApi,
  disableTotp as disableTotpApi,
  regenerateRecoveryCodes as regenerateRecoveryCodesApi,
  changePassword as changePasswordApi,
  scrapeDoctors as syncDoctorsApi,
  getScrapeProgress,
} from "../apis/userApiService"; // adjust path

import { Loader2 } from "lucide-react";
import { Country, State, City } from "country-state-city";
import { buildEhrOptions, filterAndSortEhrList } from "../constants/ehrList";
import { useSignedUrlRetry } from "../hooks/useSignedUrlRetry";
import { fetchAccountMeAvatarUrl } from "../utils/avatarUrl";

// ── Searchable Combobox with free-text insert ─────────────────────────────
function SearchableCombobox({
  options = [],
  value = "",
  onChange,
  onBlur,
  placeholder = "Search or type...",
  disabled = false,
  onDisabledClick,
  itemLabel = "option",
}) {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value ?? "");
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    setInputVal(value ?? "");
    if (!value) {
      setSearchQuery("");
    }
  }, [value]);

  const allUniqueOptions = useMemo(() => {
    const opts = Array.isArray(options) ? options : [];
    const list = [];
    const seen = new Set();
    opts.forEach(o => {
      const val = typeof o === "string" ? o : (o?.label || o?.value || "");
      if (!val) return;
      const key = val.trim().toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        list.push(val.trim());
      }
    });
    return list;
  }, [options]);

  const filtered = useMemo(() => {
    if (disabled) return [];
    const q = (searchQuery || "").trim();
    if (!q) return allUniqueOptions;
    return filterAndSortEhrList(allUniqueOptions, q);
  }, [allUniqueOptions, searchQuery, disabled]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      if (searchQuery.trim()) {
        setHighlightedIndex(filtered.length > 0 ? 0 : -1);
      } else {
        const cur = (inputVal || "").trim().toLowerCase();
        const matchIdx = filtered.findIndex(o => o.toLowerCase() === cur);
        setHighlightedIndex(matchIdx >= 0 ? matchIdx : (filtered.length > 0 ? 0 : -1));
      }
    } else {
      setHighlightedIndex(-1);
    }
  }, [open, searchQuery, filtered]);

  useEffect(() => {
    if (open && listRef.current && highlightedIndex >= 0) {
      const itemEl = listRef.current.children[highlightedIndex];
      if (itemEl && typeof itemEl.scrollIntoView === "function") {
        itemEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, open]);

  const handleSelect = (opt) => {
    if (disabled) return;
    setInputVal(opt);
    setSearchQuery("");
    onChange(opt);
    setOpen(false);
    onBlur?.(opt);
  };

  const handleClear = () => {
    if (disabled) return;
    setInputVal("");
    setSearchQuery("");
    onChange("");
    onBlur?.("");
    setOpen(true);
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    if (disabled) return;
    const v = e.target.value;
    setInputVal(v);
    setSearchQuery(v);
    setOpen(true);
    onChange(v);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!open) onBlur?.(inputVal);
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else if (filtered.length > 0) {
        setHighlightedIndex(prev => (prev < filtered.length - 1 ? prev + 1 : 0));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else if (filtered.length > 0) {
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : filtered.length - 1));
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
        handleSelect(filtered[highlightedIndex]);
      } else if (filtered.length > 0) {
        handleSelect(filtered[0]);
      } else if (inputVal.trim()) {
        handleSelect(inputVal.trim());
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setSearchQuery("");
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full mt-2 ${open ? "z-50" : ""} ${disabled ? "cursor-not-allowed" : ""}`}
      onClick={() => {
        if (disabled && onDisabledClick) {
          onDisabledClick();
        }
      }}
    >
      <div className="relative flex items-center">
        <Input
          ref={inputRef}
          value={inputVal}
          disabled={disabled}
          onChange={handleInputChange}
          onFocus={(e) => {
            if (!disabled) {
              setOpen(true);
              setTimeout(() => {
                e.target?.select();
              }, 0);
            }
          }}
          onClick={() => {
            if (!disabled && !open) {
              setSearchQuery("");
              setOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`h-11 w-full rounded-xl border border-slate-200 bg-white pl-4 pr-16 text-sm text-slate-900 shadow-sm transition-all focus:border-blue-400 focus:ring-1 focus:ring-blue-400 ${
            disabled ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""
          }`}
        />

        {/* Action Buttons: Clear (X) + Dropdown Toggle (Chevron) */}
        {!disabled && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 z-10">
            {Boolean(inputVal) && (
              <button
                type="button"
                title="Clear"
                className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-slate-100 transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear();
                }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              title={open ? "Close options" : "Show all options"}
              className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSearchQuery("");
                setOpen(prev => !prev);
                inputRef.current?.focus();
              }}
            >
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>
          </div>
        )}
      </div>

      {!disabled && open && (
        <div ref={listRef} className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl custom-scrollbar py-1">
          {filtered.length > 0 ? (
            <>
              {filtered.map((opt, idx) => {
                const isSelected = Boolean(inputVal && opt.toLowerCase() === inputVal.trim().toLowerCase());
                const isHighlighted = idx === highlightedIndex;
                return (
                  <div
                    key={idx}
                    className={`cursor-pointer px-3.5 py-2 text-sm sm:text-sm flex items-center justify-between transition-colors ${
                      isSelected && isHighlighted
                        ? "bg-blue-100 text-blue-900 font-medium"
                        : isHighlighted
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : isSelected
                        ? "bg-blue-50 text-blue-900 font-medium"
                        : "text-slate-800 hover:bg-slate-50"
                    }`}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(opt);
                    }}
                  >
                    <span className="truncate pr-2" title={opt}>{opt}</span>
                    {isSelected && <Check className="h-4 w-4 text-blue-600 shrink-0" />}
                  </div>
                );
              })}
              {Boolean(inputVal.trim()) && !filtered.some(f => f.toLowerCase() === inputVal.trim().toLowerCase()) && (
                <div
                  className="cursor-pointer px-4 py-2.5 text-xs text-blue-600 hover:bg-blue-50 font-medium flex items-center justify-between border-t border-slate-100 mt-1"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(inputVal.trim());
                  }}
                >
                  <span>Use "<strong>{inputVal.trim()}</strong>" as custom {itemLabel}</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">Select</span>
                </div>
              )}
            </>
          ) : inputVal.trim() ? (
            <div
              className="cursor-pointer px-4 py-2.5 text-xs text-blue-600 hover:bg-blue-50 font-medium flex items-center justify-between"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(inputVal.trim());
              }}
            >
              <span>Use "<strong>{inputVal.trim()}</strong>" as custom {itemLabel}</span>
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">Select</span>
            </div>
          ) : (
            <div className="px-4 py-3 text-xs text-slate-400 text-center italic">
              No {itemLabel} options available
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileSettingsSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start animate-pulse">
      {/* LEFT COLUMN: CLINICIAN & PRACTICE SKELETON */}
      <div className="space-y-6">
        {/* Card 1: Personal Profile */}
        <div className="border border-slate-200/80 bg-white shadow-xs rounded-2xl overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-slate-200 shrink-0" />
              <div className="space-y-1.5 flex-1">
                <div className="h-5 w-36 rounded-md bg-slate-200" />
                <div className="h-3 w-52 rounded-md bg-slate-100" />
              </div>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5 p-4 rounded-xl border border-slate-100 bg-slate-50/40">
              <div className="h-20 w-20 rounded-full bg-slate-200 shrink-0" />
              <div className="space-y-2 flex-1">
                <div className="h-4 w-28 rounded-md bg-slate-200" />
                <div className="h-3 w-56 rounded-md bg-slate-100" />
                <div className="h-9 w-32 rounded-lg bg-slate-200 mt-2" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-[100px,1fr,1fr]">
              <div className="space-y-1.5">
                <div className="h-3 w-10 rounded bg-slate-200" />
                <div className="h-10 rounded-xl bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <div className="h-3 w-16 rounded bg-slate-200" />
                <div className="h-10 rounded-xl bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <div className="h-3 w-16 rounded bg-slate-200" />
                <div className="h-10 rounded-xl bg-slate-100" />
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <div className="h-3 w-16 rounded bg-slate-200" />
                <div className="h-10 rounded-xl bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <div className="h-3 w-24 rounded bg-slate-200" />
                <div className="h-10 rounded-xl bg-slate-100" />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Practice & Workplace */}
        <div className="border border-slate-200/80 bg-white shadow-xs rounded-2xl overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-slate-200 shrink-0" />
              <div className="space-y-1.5 flex-1">
                <div className="h-5 w-44 rounded-md bg-slate-200" />
                <div className="h-3 w-64 rounded-md bg-slate-100" />
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <div className="h-3 w-24 rounded bg-slate-200" />
                <div className="h-11 rounded-xl bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <div className="h-3 w-24 rounded bg-slate-200" />
                <div className="h-11 rounded-xl bg-slate-100" />
              </div>
            </div>
            <div className="space-y-1.5 pt-1">
              <div className="h-3 w-20 rounded bg-slate-200" />
              <div className="h-11 rounded-xl bg-slate-100" />
            </div>
          </div>
        </div>

        {/* Card 3: Default Location */}
        <div className="border border-slate-200/80 bg-white shadow-xs rounded-2xl overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-slate-200 shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-5 w-36 rounded-md bg-slate-200" />
                  <div className="h-3 w-56 rounded-md bg-slate-100" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-8.5 w-28 rounded-lg bg-slate-200" />
                <div className="h-8.5 w-20 rounded-lg bg-slate-200" />
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-1.5">
              <div className="h-3 w-24 rounded bg-slate-200" />
              <div className="h-10 rounded-xl bg-slate-100" />
            </div>
            <div className="grid gap-3.5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <div className="h-3 w-16 rounded bg-slate-200" />
                <div className="h-10 rounded-xl bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <div className="h-3 w-24 rounded bg-slate-200" />
                <div className="h-10 rounded-xl bg-slate-100" />
              </div>
            </div>
            <div className="grid gap-3.5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <div className="h-3 w-12 rounded bg-slate-200" />
                <div className="h-10 rounded-xl bg-slate-100" />
              </div>
              <div className="space-y-1.5">
                <div className="h-3 w-16 rounded bg-slate-200" />
                <div className="h-10 rounded-xl bg-slate-100" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN: PREFERENCES & SECURITY SKELETON */}
      <div className="space-y-6">
        {/* Card 4: SOAP Note Display */}
        <div className="border border-slate-200/80 bg-white shadow-xs rounded-2xl overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-slate-200 shrink-0" />
              <div className="space-y-1.5 flex-1">
                <div className="h-5 w-40 rounded-md bg-slate-200" />
                <div className="h-3 w-60 rounded-md bg-slate-100" />
              </div>
            </div>
          </div>
          <div className="p-6 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 p-4 border border-slate-200/80 rounded-xl bg-slate-50/50">
              <div className="space-y-1.5 min-w-0 flex-1">
                <div className="h-4 w-32 rounded-md bg-slate-200" />
                <div className="h-3 w-full max-w-sm rounded-md bg-slate-100" />
              </div>
              <div className="h-6 w-11 rounded-full bg-slate-200 shrink-0" />
            </div>
            <div className="rounded-xl border border-dashed border-slate-200 bg-white p-4 space-y-2">
              <div className="h-3 w-20 rounded bg-slate-200" />
              <div className="h-20 w-full rounded-lg bg-slate-50 border border-slate-100" />
              <div className="h-2.5 w-64 rounded bg-slate-100" />
            </div>
          </div>
        </div>

        {/* Card 5: Security & Authentication */}
        <div className="border border-slate-200/80 bg-white shadow-xs rounded-2xl overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-slate-200 shrink-0" />
              <div className="space-y-1.5 flex-1">
                <div className="h-5 w-48 rounded-md bg-slate-200" />
                <div className="h-3 w-72 rounded-md bg-slate-100" />
              </div>
            </div>
          </div>
          <div className="p-6 space-y-6">
            <div className="space-y-1.5">
              <div className="h-3 w-24 rounded bg-slate-200" />
              <div className="h-10 rounded-xl bg-slate-100" />
              <div className="h-2.5 w-56 rounded bg-slate-100" />
            </div>
            <div className="h-px bg-slate-100" />
            <div className="space-y-3">
              <div className="h-4 w-44 rounded-md bg-slate-200" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div className="space-y-1">
                  <div className="h-3 w-24 rounded bg-slate-200" />
                  <div className="h-10 rounded-xl bg-slate-100" />
                </div>
                <div className="space-y-1">
                  <div className="h-3 w-28 rounded bg-slate-200" />
                  <div className="h-10 rounded-xl bg-slate-100" />
                </div>
              </div>
            </div>
            <div className="h-px bg-slate-100" />
            <div className="space-y-3">
              <div className="h-4 w-52 rounded-md bg-slate-200" />
              <div className="h-3 w-72 rounded-md bg-slate-100" />
              <div className="h-24 w-full rounded-2xl bg-slate-50 border border-slate-200/80" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProfileSettings({ specialties = [], options = {} }) {
  const { refreshMe } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [title, setTitle] = useState(undefined);
  const [companySize, setCompanySize] = useState(undefined);

  const [selectedSpecialty, setSelectedSpecialty] = useState(undefined);
  const [avatarUrl, setAvatarUrl] = useState("");
  // Signed avatar URLs expire: on load failure refetch /account/me once, then show the initials fallback.
  const { src: avatarSrc, onLoadingStatusChange: onAvatarLoadingStatusChange } =
    useSignedUrlRetry(avatarUrl, fetchAccountMeAvatarUrl);
  const [email, setEmail] = useState("");

  const [organization, setOrganization] = useState("");
  const [practiceName, setPracticeName] = useState("");

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("");

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);

  const [uniquePractices, setUniquePractices] = useState([]);

  const [ehrSystem, setEhrSystem] = useState("");
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpStep, setTotpStep] = useState("idle"); // "idle" | "setup"
  const [totpSecret, setTotpSecret] = useState("");
  const [totpQrUrl, setTotpQrUrl] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [totpLoading, setTotpLoading] = useState(false);
  const [showDisableConfirmModal, setShowDisableConfirmModal] = useState(false);
  const [showSwitchConfirmModal, setShowSwitchConfirmModal] = useState(false);
  // Recovery codes state
  const [showRecoveryCodesModal, setShowRecoveryCodesModal] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [regenTotpCode, setRegenTotpCode] = useState("");
  const [showRegenInput, setShowRegenInput] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [emailVerified, setEmailVerified] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Change password
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwMsg, setPwMsg] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [unlockedInputs, setUnlockedInputs] = useState({});
  const unlockInput = (field) => setUnlockedInputs((prev) => ({ ...prev, [field]: true }));
  const [soapTypingAnimationEnabled, setSoapTypingAnimationEnabled] = useState(false);
  const [typingExampleText, setTypingExampleText] = useState("");

  const criteria = [
    { label: "At least 8 characters", test: (p) => p.length >= 8 },
    { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
    { label: "One number", test: (p) => /[0-9]/.test(p) },
    { label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
  ];

  const passedCount = criteria.filter((c) => c.test(newPassword)).length;

  const strengthConfig = [
    { label: "", color: "bg-gray-200" },
    { label: "Weak", color: "bg-red-400" },
    { label: "Fair", color: "bg-orange-400" },
    { label: "Good", color: "bg-yellow-400" },
    { label: "Strong", color: "bg-green-500" },
  ];
  const { label: strengthLabel, color: strengthColor } = strengthConfig[passedCount];

  // Doctor Search State
  const [syncingDoctors, setSyncingDoctors] = useState(false);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    const storedJobId = localStorage.getItem("scrapeJobId");
    if (storedJobId) {
      setSyncingDoctors(true);
      startPollingProgress(storedJobId);
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  // Live example of the SOAP note typewriter effect (runs when the preference is on)
  useEffect(() => {
    const SAMPLE =
      "Subjective: Patient reports mild headache for 2 days, no fever. Objective: Alert, vitals stable. Assessment: Tension headache. Plan: Hydration, follow up PRN.";
    if (!soapTypingAnimationEnabled) {
      setTypingExampleText(SAMPLE);
      return;
    }
    setTypingExampleText("");
    let i = 0;
    const tick = setInterval(() => {
      i = Math.min(SAMPLE.length, i + 2);
      setTypingExampleText(SAMPLE.slice(0, i));
      if (i >= SAMPLE.length) clearInterval(tick);
    }, 28);
    return () => clearInterval(tick);
  }, [soapTypingAnimationEnabled]);

  const startPollingProgress = (jobId) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = setInterval(async () => {
      try {
        const progressRes = await getScrapeProgress(jobId);
        const job = progressRes.data?.data;
        if (job) {
          if (job.status === 'pending') {
            setSyncingDoctors(true);
          } else if (job.status === 'completed' || job.status === 'error') {
            clearInterval(pollIntervalRef.current);
            setSyncingDoctors(false);
            localStorage.removeItem("scrapeJobId");
            localStorage.removeItem("scrapeFormNumber");
          }
        }
      } catch (e) {
        const status = e.response?.status;
        if (status === 404 || status === 403) {
          clearInterval(pollIntervalRef.current);
          setSyncingDoctors(false);
          localStorage.removeItem("scrapeJobId");
          localStorage.removeItem("scrapeFormNumber");
        }
      }
    }, 8000);
  };


  const titleOptions = useMemo(() => {
    const list = Array.isArray(options.titles) && options.titles.length
      ? options.titles
      : ["MD", "DO", "NP", "PA", "Others"];
    return list;
  }, [options]);

  const companySizeOptions = useMemo(() => {
    const list = Array.isArray(options.companySizes) && options.companySizes.length
      ? options.companySizes
      : ["Solo Practitioner", "2-5", "6-10", "10+"];
    return list;
  }, [options]);

  const ehrOptions = useMemo(() => {
    const list = Array.isArray(options.ehrSystems) ? options.ehrSystems : [];
    return buildEhrOptions(list);
  }, [options.ehrSystems]);

  const specialtySource = useMemo(() => {
    const src = options.specialties && options.specialties.length ? options.specialties : specialties;
    return Array.isArray(src) ? src : [];
  }, [options, specialties]);



  const initials = useMemo(() => {
    const f = (firstName || "").trim();
    const l = (lastName || "").trim();
    return (f || l) ? `${f?.[0] || ""}${l?.[0] || ""}`.toUpperCase() : "";
  }, [firstName, lastName]);

  const specialtyOptions = useMemo(() => {
    const base = [...specialtySource];
    if (selectedSpecialty && !base.includes(selectedSpecialty)) {
      base.unshift(selectedSpecialty);
    }
    if (!base.includes("Other")) {
      base.push("Other");
    }
    return base;
  }, [specialtySource, selectedSpecialty]);

  const allCountries = useMemo(() => Country.getAllCountries().map(c => ({ label: c.name, value: c.isoCode, ...c })), []);
  const states = useMemo(() => selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode).map(s => ({ label: s.name, value: s.isoCode, ...s })) : [], [selectedCountry]);
  const cities = useMemo(() => selectedState ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map(c => ({ label: c.name, value: c.name, ...c })) : [], [selectedCountry, selectedState]);



  const practiceOptions = useMemo(() => {
    const base = uniquePractices.filter(b => (b.value || "").toLowerCase() !== "other" && (b.label || "").toLowerCase() !== "other");
    if (practiceName && practiceName.toLowerCase() !== "other" && !base.some(b => b.value === practiceName)) {
      base.unshift({ label: practiceName, value: practiceName });
    }
    return base;
  }, [uniquePractices, practiceName]);

  const normalize = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, typeof v === "string" && v.trim() === "" ? null : v])
    );

  const updateProfile = async (partial) => {
    try {
      await updateProfileApi(normalize(partial));
    } catch (e) {
      console.error("Profile update failed:", e?.response?.data || e.message);
    }
  };

  // ✅ Load profile data
  useEffect(() => {
    (async () => {
      try {
        const [res, pNamesRes] = await Promise.all([
          getMyProfile({ includePartners: true }),
          getPracticeNames({ defaultOnly: true })
        ]);
        const root = res?.data;
        const user =
          root?.user ?? root?.data?.user ?? root?.body?.data?.user;
        const profile =
          root?.profile ?? root?.data?.profile ?? root?.body?.data?.profile;

        if (user?.email) setEmail(user.email);
        if (typeof user?.hasPassword === 'boolean') setHasPassword(Boolean(user.hasPassword));
        setEmailVerified(Boolean(user?.emailVerified));

        if (profile) {
          setFirstName(profile.firstName ?? "");
          setLastName(profile.lastName ?? "");
          setAvatarUrl(profile.profileimage ?? "");

          setTitle(profile.title ?? undefined);
          setOrganization((profile.organization ?? "").trim());

          const rawPNames = pNamesRes?.data?.practiceNames || pNamesRes?.data?.body?.data?.practiceNames || pNamesRes?.data?.data?.practiceNames;
          const practicesArr = Array.isArray(rawPNames) ? rawPNames : (Array.isArray(pNamesRes?.data) ? pNamesRes.data : []);

          const pName = (profile.practiceName ?? "").trim();
          const pNameLower = pName.toLowerCase();

          // Extra safety: exclude any practice names that strictly belong to extra locations
          const extraLocPractices = new Set();
          if (profile.extraLocations) {
            try {
              const parsedExtra = typeof profile.extraLocations === 'string' ? JSON.parse(profile.extraLocations) : profile.extraLocations;
              if (Array.isArray(parsedExtra)) {
                parsedExtra.forEach(loc => {
                  const locName = (loc?.practiceName || loc?.practice || loc?.organization || "").trim().toLowerCase();
                  if (locName && locName !== pNameLower) extraLocPractices.add(locName);
                  let locPartners = loc?.practicePartners || [];
                  if (typeof locPartners === 'string') {
                    try { locPartners = JSON.parse(locPartners); } catch { locPartners = []; }
                  }
                  if (Array.isArray(locPartners)) {
                    locPartners.forEach(lp => {
                      const lpName = (lp?.practice || lp?.practiceName || lp?.organization || "").trim().toLowerCase();
                      if (lpName && lpName !== pNameLower) extraLocPractices.add(lpName);
                    });
                  }
                });
              }
            } catch { /* ignore */ }
          }

          const defaultPractices = practicesArr.filter(p => !extraLocPractices.has(String(p).trim().toLowerCase()));

          // Parse default location partners to include every full practice name field
          let partners = profile.practicePartners;
          if (typeof partners === 'string') {
            try { partners = JSON.parse(partners); } catch { partners = []; }
          }
          if (!Array.isArray(partners)) partners = [];

          const seen = new Set();
          const combinedList = [];
          const addOption = (raw) => {
            if (!raw || typeof raw !== 'string') return;
            const trimmed = raw.trim();
            if (trimmed && trimmed.toLowerCase() !== "other" && !seen.has(trimmed.toLowerCase())) {
              seen.add(trimmed.toLowerCase());
              combinedList.push({ label: trimmed, value: trimmed });
            }
          };

          // 1. Current assigned practice name
          addOption(pName);

          // 2. Default location partners (full practice name fields)
          partners.forEach(p => {
            if (p && typeof p === 'object') {
              addOption(p.practice);
              addOption(p.practiceName);
              addOption(p.originalPractice);
              addOption(p.organization);
              addOption(p.clinic);
              if (p.latestScraped && typeof p.latestScraped === 'object') {
                addOption(p.latestScraped.practice);
                addOption(p.latestScraped.practiceName);
                addOption(p.latestScraped.originalPractice);
                addOption(p.latestScraped.organization);
                addOption(p.latestScraped.clinic);
              }
            }
          });

          // 3. Any practices returned from getPracticeNames API
          defaultPractices.forEach(p => {
            const val = typeof p === 'string' ? p : (p?.value || p?.label);
            addOption(val);
          });

          combinedList.sort((a, b) => a.label.localeCompare(b.label));
          setUniquePractices(combinedList);

          setPracticeName(pName);

          setAddress((profile.address ?? "").trim());
          setCity((profile.city ?? "").trim());
          setState((profile.state ?? "").trim());
          setZipCode((profile.zipCode ?? "").trim());
          setCountry((profile.country ?? "").trim());

          if (profile.country) {
            const c = Country.getAllCountries().map(x => ({ label: x.name, value: x.isoCode, ...x })).find(x => x.name === profile.country || x.isoCode === profile.country);
            if (c) {
              setSelectedCountry(c);
              if (profile.state) {
                const sList = State.getStatesOfCountry(c.isoCode).map(s => ({ label: s.name, value: s.isoCode, ...s }));
                const s = sList.find(x => x.name === profile.state || x.isoCode === profile.state);
                if (s) setSelectedState(s);
              }
            }
          }

          const ehrValue = (profile.ehrSystem ?? "").trim();
          setEhrSystem(ehrValue);
          setCompanySize(profile.companySize ?? undefined);
          setSoapTypingAnimationEnabled(Boolean(profile.soapTypingAnimationEnabled));
          setMfaEnabled(Boolean(profile.mfaEnabled));
          setTotpEnabled(Boolean(profile.totpEnabled));
          setSelectedSpecialty(profile.specialty || undefined);
        }
      } catch (e) {
        console.error("Load profile error:", e?.response?.data || e.message);
      } finally {
        setLoaded(true);
      }
    })();
  }, [options, specialties]);

  // Avatar
  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 1. Validate file type
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/jpg"];
    if (!validTypes.includes(file.type.toLowerCase())) {
      toast.error("Please select a valid image file (JPG, PNG, or WebP).");
      e.target.value = "";
      return;
    }

    // 2. Validate file size (2MB max)
    const MAX_SIZE_MB = 2;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    if (file.size > MAX_SIZE_BYTES) {
      const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`File is too large (${fileSizeInMB}MB). Maximum allowed size is ${MAX_SIZE_MB}MB.`);
      e.target.value = "";
      return;
    }

    const previousAvatarUrl = avatarUrl;

    // Local preview
    const reader = new FileReader();
    reader.onloadend = () => setAvatarUrl(String(reader.result || ""));
    reader.readAsDataURL(file);

    const toastId = toast.loading("Uploading photo...");
    try {
      setUploading(true);
      const res = await uploadAvatarApi(file);
      const newUrl = res?.data?.profileimage || res?.profileimage;
      if (newUrl) {
        setAvatarUrl(newUrl);
        toast.success("Profile photo updated!", { id: toastId });
      } else {
        toast.error("Avatar upload failed", { id: toastId });
      }
      try {
        await refreshMe();
      } catch (refreshError) {
        console.error("Unable to refresh profile after avatar upload:", refreshError);
      }
    } catch (err) {
      console.error("Avatar upload error:", err?.response?.data || err.message);
      // Revert preview on failure
      setAvatarUrl(previousAvatarUrl);
      const msg =
        err?.response?.data?.body?.message ||
        err?.response?.data?.message ||
        "Failed to upload photo. Please ensure it is under 2MB.";
      toast.error(msg, { id: toastId });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarUrl("");
    await updateProfile({ profileimage: null });
    try {
      await refreshMe();
    } catch (refreshError) {
      console.error("Unable to refresh profile after avatar removal:", refreshError);
    }
  };

  // ✅ MFA switchable (true/false)
  const onToggleMfa = async (checked) => {
    const prev = mfaEnabled;
    setMfaEnabled(checked);
    const toastId = toast.loading(`${checked ? "Enabling" : "Disabling"} Email MFA...`);
    try {
      await enableMfaApi(checked);
      toast.success(`Email verification codes ${checked ? "enabled" : "disabled"} successfully!`, { id: toastId });
      await refreshMe();
    } catch (e) {
      console.error("MFA toggle error:", e?.response?.data || e.message);
      setMfaEnabled(prev); // rollback on failure
      toast.error(e?.response?.data?.body?.message || `Failed to update Email MFA settings.`, { id: toastId });
    }
  };

  const handleInitiateTotp = async () => {
    setTotpLoading(true);
    try {
      const res = await setupTotpApi();
      const data = res.data || res;
      setTotpSecret(data.secret || "");
      setTotpQrUrl(data.qrCodeUrl || "");
      setTotpStep("setup");
    } catch (e) {
      console.error("Failed to initiate TOTP setup:", e);
      toast.error("Failed to initiate authenticator setup. Please try again.");
    } finally {
      setTotpLoading(false);
    }
  };

  const handleVerifyAndEnableTotp = async () => {
    if (!totpCode || totpCode.trim().length !== 6) {
      toast.error("Please enter a valid 6-digit verification code");
      return;
    }
    setTotpLoading(true);
    try {
      const res = await verifyTotpApi(totpCode.trim());
      const data = res.data || res;
      setTotpEnabled(true);
      setTotpStep("idle");
      setTotpCode("");
      toast.success("Authenticator app 2FA enabled successfully!");
      // Show recovery codes modal if returned
      const codes = data.recoveryCodes || data?.data?.recoveryCodes;
      if (codes && codes.length > 0) {
        setRecoveryCodes(codes);
        setShowRecoveryCodesModal(true);
      }
      await refreshMe();
    } catch (e) {
      console.error("Failed to verify TOTP:", e);
      toast.error(e?.response?.data?.body?.message || "Invalid verification code. Please try again.");
    } finally {
      setTotpLoading(false);
    }
  };

  const confirmDisableTotp = async () => {
    setShowDisableConfirmModal(false);
    setTotpLoading(true);
    try {
      // Disable both Authenticator TOTP and Email MFA to completely downgrade to "Basic Security (Password Only)"
      await disableTotpApi();
      setTotpEnabled(false);
      await enableMfaApi(false);
      setMfaEnabled(false);
      setTotpStep("idle");
      toast.success("Two-factor authentication disabled successfully.");
      await refreshMe();
    } catch (e) {
      console.error("Failed to disable 2FA:", e);
      toast.error("Failed to disable 2FA. Please try again.");
    } finally {
      setTotpLoading(false);
    }
  };

  const confirmSwitchToEmail = async () => {
    setShowSwitchConfirmModal(false);
    setTotpLoading(true);
    try {
      await disableTotpApi();
      setTotpEnabled(false);
      await enableMfaApi(true);
      setMfaEnabled(true);
      toast.success("Switched to Email Verification Codes successfully!");
      await refreshMe();
    } catch (e) {
      toast.error("Failed to switch to Email Codes. Please try again.");
    } finally {
      setTotpLoading(false);
    }
  };

  const handleGrabLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    const toastId = toast.loading("Fetching location...");
    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&accept-language=en`);
        if (!response.ok) throw new Error("Location fetch failed");

        const data = await response.json();
        const addr = data.address;

        if (addr) {
          const newCountry = addr.country;
          const newState = addr.state;
          const newCity = addr.city || addr.town || addr.village || addr.municipality || addr.county || "";
          const newZip = addr.postcode || "";
          const road = addr.road || addr.pedestrian || addr.street || "";
          const house_number = addr.house_number || "";
          const area = addr.suburb || addr.neighbourhood || addr.quarter || addr.residential || "";

          let newAddress = `${house_number} ${road}`.trim();
          if (!newAddress && area) {
            newAddress = area;
          } else if (newAddress && area) {
            newAddress = `${newAddress}, ${area}`;
          }

          setCountry(newCountry);
          setState(newState);
          setCity(newCity);
          setZipCode(newZip);
          setAddress(newAddress);

          const c = allCountries.find(x => x.name === newCountry || x.isoCode === newCountry);
          if (c) {
            setSelectedCountry(c);
            const sl = State.getStatesOfCountry(c.isoCode);
            const s = sl.find(x => x.name === newState || x.isoCode === newState);
            if (s) setSelectedState({ label: s.name, value: s.isoCode, ...s });
          }

          await updateProfile({ country: newCountry, state: newState, city: newCity, zipCode: newZip, address: newAddress });
          toast.success("Location updated", { id: toastId });
        } else {
          toast.error("Could not determine address", { id: toastId });
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to get location details", { id: toastId });
      }
    }, (error) => {
      console.error(error);
      toast.error("Geolocation error: " + error.message, { id: toastId });
    }, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    });
  };

  const handleSyncNearby = async () => {
    if (!zipCode) return toast.error("Please ensure a Zip Code is configured to fetch nearby providers.");
    try {
      setSyncingDoctors(true);
      const res = await syncDoctorsApi({
        address: address,
        city: city,
        zipCode: zipCode,
        state: state,
        country: country,
        formNumber: 1
      });
      if (res.data.success && res.data.jobId) {
        localStorage.setItem("scrapeJobId", res.data.jobId);
        localStorage.setItem("scrapeFormNumber", 1);
        toast.success("Providers Directory sync initiated. Check the Providers Directory tab for progress.");
        startPollingProgress(res.data.jobId);
      } else {
        toast.success("Updated nearby doctors");
        setSyncingDoctors(false);
      }
    } catch (e) {
      toast.error("Sync failed: " + (e.response?.data?.message || e.message));
      setSyncingDoctors(false);
    }
  };

  return (
    <div className="w-full max-w-[96%] xl:max-w-[1600px] 2xl:max-w-[1750px] mx-auto pb-16 pt-2 px-2 sm:px-4">
      {!loaded ? (
        <ProfileSettingsSkeleton />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* ═════════════════════════════════════════════════════════════
            LEFT COLUMN: CLINICIAN & PRACTICE INFORMATION
        ═════════════════════════════════════════════════════════════ */}
        <div className="space-y-6">
          {/* ─────────────────────────────────────────────────────────────
              1. PERSONAL PROFILE & CREDENTIALS
          ───────────────────────────────────────────────────────────── */}
          <Card className="relative z-30 focus-within:z-50 border border-slate-200/80 bg-white shadow-xs hover:shadow-sm transition-shadow rounded-2xl">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 border border-blue-100/80 shadow-2xs">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-slate-800">Personal Profile</CardTitle>
                  <CardDescription className="text-xs text-slate-500 mt-0.5">
                    Manage your personal identity.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-6">
              {/* Avatar Section */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-5 p-4 rounded-xl border border-slate-100 bg-slate-50/40">
                <Avatar className="h-20 w-20 border-2 border-slate-200 shadow-xs rounded-full overflow-hidden bg-white">
                  <AvatarImage
                    src={avatarSrc}
                    alt="avatar"
                    referrerPolicy="no-referrer"
                    onLoadingStatusChange={onAvatarLoadingStatusChange}
                  />
                  <AvatarFallback className="text-xl font-bold bg-gradient-to-br from-blue-500 to-indigo-600 text-white uppercase tracking-wider">
                    {initials || "MD"}
                  </AvatarFallback>
                </Avatar>

                <div className="space-y-2 flex-1">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">Profile Photo</h4>
                    <p className="text-xs text-slate-500 mt-0.5">Upload a JPG, PNG, or WebP image up to 2MB.</p>
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    id="upload-avatar"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 px-3.5 text-xs font-medium border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-2xs rounded-lg transition-colors"
                      onClick={() => document.getElementById("upload-avatar")?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      ) : (
                        <Upload className="h-3.5 w-3.5 mr-1.5 text-slate-500" />
                      )}
                      {uploading ? "Uploading..." : "Upload Photo"}
                    </Button>
                    {avatarUrl && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRemoveAvatar}
                        className="h-9 px-3 text-xs font-medium border-slate-200 bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 text-slate-600 shadow-2xs rounded-lg transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Title / Names Grid */}
              <div className="grid gap-3 sm:grid-cols-[100px,1fr,1fr]">
                <div>
                  <Label htmlFor="title" className="text-xs font-semibold text-slate-700 mb-1.5 block">Title</Label>
                  <Select
                    key={`title-${loaded}`}
                    value={title || undefined}
                    onValueChange={async (value) => {
                      setTitle(value);
                      let newRole = "";
                      if (value === "MD") newRole = "Doctor of Medicine";
                      else if (value === "DO") newRole = "Doctor of Osteopathic Medicine";
                      else if (value === "NP") newRole = "Nurse Practitioner";
                      else if (value === "PA") newRole = "Physician Assistant";
                      else if (value === "Others" || value === "Other") newRole = "Administrator";

                      await updateProfile({ title: value || null, roleTitle: newRole || null });
                      await refreshMe();
                    }}
                  >
                    <SelectTrigger id="title" className="h-10 w-full bg-white border border-slate-200 rounded-xl px-3 text-sm text-slate-900 shadow-2xs focus:ring-2 focus:ring-blue-500">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-xl shadow-lg border border-slate-200">
                      {titleOptions.map((opt) => (
                        <SelectItem key={opt} value={opt} className="hover:bg-blue-50 cursor-pointer">
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="firstName" className="text-xs font-semibold text-slate-700 mb-1.5 block">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onBlur={() => updateProfile({ firstName })}
                    placeholder="First name"
                    className="h-10 rounded-xl border-slate-200 bg-white text-sm shadow-2xs"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-xs font-semibold text-slate-700 mb-1.5 block">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onBlur={() => updateProfile({ lastName })}
                    placeholder="Last name"
                    className="h-10 rounded-xl border-slate-200 bg-white text-sm shadow-2xs"
                  />
                </div>
              </div>

              {/* Specialty & Company Size */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Specialty</Label>
                  <SingleSelectDropdown
                    options={specialtyOptions}
                    placeholder="Select specialty"
                    value={selectedSpecialty ?? undefined}
                    onSelect={async (value) => {
                      const v = typeof value === "string" ? value : (value?.value ?? value?.label);
                      setSelectedSpecialty(v || undefined);
                      await updateProfile({ specialty: v || null });
                    }}
                    triggerClassName="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-left text-sm text-slate-900 shadow-2xs"
                    panelClassName="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-xl"
                    searchWrapperClassName="flex items-center border-b border-slate-200 p-2"
                    searchIconClassName="mr-2 h-4 w-4 text-slate-400"
                    searchInputClassName="w-full text-sm outline-none placeholder:text-slate-400"
                    listClassName="max-h-60 overflow-y-auto"
                    optionClassName="px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-600"
                    emptyStateClassName="px-3 py-2 text-sm text-slate-500"
                  />
                </div>
                <div>
                  <Label htmlFor="companySize" className="text-xs font-semibold text-slate-700 mb-1.5 block">Company Size</Label>
                  <Select
                    key={`cs-${loaded}`}
                    name="companySize"
                    value={companySize ?? undefined}
                    onValueChange={async (v) => {
                      setCompanySize(v);
                      await updateProfile({ companySize: v });
                    }}
                  >
                    <SelectTrigger id="companySize" className="h-10 w-full bg-white border border-slate-200 rounded-xl px-3 text-sm text-slate-900 shadow-2xs">
                      <SelectValue placeholder="Select company size" />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-xl shadow-lg border border-slate-200">
                      {companySizeOptions.map((option) => (
                        <SelectItem key={option} value={option} className="hover:bg-blue-50 cursor-pointer">
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ─────────────────────────────────────────────────────────────
              2. PRACTICE & WORKPLACE
          ───────────────────────────────────────────────────────────── */}
          <Card className="relative z-20 focus-within:z-50 border border-slate-200/80 bg-white shadow-xs hover:shadow-sm transition-shadow rounded-2xl">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-600 border border-teal-100/80 shadow-2xs">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-slate-800">Practice & Workplace</CardTitle>
                  <CardDescription className="text-xs text-slate-500 mt-0.5">
                    Specify your practice, organization, and electronic health record system.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="practiceName" className="text-xs font-semibold text-slate-700">Practice Name</Label>
                  <SearchableCombobox
                    options={practiceOptions}
                    value={practiceName || ""}
                    onChange={(v) => setPracticeName(v)}
                    onBlur={(v) => {
                      const val = (v || "").trim();
                      setPracticeName(val);
                      updateProfile({ practiceName: val || null });
                    }}
                    placeholder="e.g., Lakeside Family Medicine"
                    itemLabel="practice"
                  />
                </div>

                <div>
                  <Label htmlFor="organization" className="text-xs font-semibold text-slate-700">Organization</Label>
                  <Input
                    id="organization"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    onBlur={(e) => {
                      const val = e.target.value.trim();
                      setOrganization(val);
                      updateProfile({ organization: val || null });
                    }}
                    placeholder="e.g., Progressive Health Group"
                    className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 shadow-sm mt-2 transition-all focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  />
                </div>
              </div>

              <div className="pt-1">
                <Label htmlFor="ehrSystem" className="text-xs font-semibold text-slate-700">EHR System</Label>
                <SearchableCombobox
                  options={ehrOptions}
                  value={ehrSystem || ""}
                  onChange={(v) => setEhrSystem(v)}
                  onBlur={async (v) => {
                    const val = (v || "").trim();
                    setEhrSystem(val);
                    await updateProfile({ ehrSystem: val || null });
                  }}
                  placeholder="Search or type your EHR system (e.g. Epic, Cerner, AthenaHealth)"
                  itemLabel="EHR"
                />
              </div>
            </CardContent>
          </Card>

          {/* ─────────────────────────────────────────────────────────────
              3. PRIMARY CLINICAL LOCATION
          ───────────────────────────────────────────────────────────── */}
          <Card className="relative z-10 focus-within:z-50 border border-slate-200/80 bg-white shadow-xs hover:shadow-sm transition-shadow rounded-2xl">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-5 rounded-t-2xl">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-600 border border-sky-100/80 shadow-2xs">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-bold text-slate-800">Default Location</CardTitle>
                    <CardDescription className="text-xs text-slate-500 mt-0.5">
                      Set your default location to sync nearby doctors.
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8.5 text-xs font-medium text-blue-600 border-blue-200 bg-blue-50/50 hover:bg-blue-100/70 hover:text-blue-700 rounded-lg px-2.5 shadow-2xs transition-colors"
                    onClick={handleGrabLocation}
                  >
                    <MapPin className="mr-1 h-3.5 w-3.5" /> Grab Location
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8.5 text-xs font-medium text-slate-700 border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-2.5 shadow-2xs transition-colors"
                    onClick={handleSyncNearby}
                    disabled={!zipCode || syncingDoctors}
                  >
                    <RefreshCw className={`mr-1 h-3.5 w-3.5 ${syncingDoctors ? "animate-spin text-blue-600" : "text-slate-500"}`} />
                    {syncingDoctors ? "Syncing..." : "Sync"}
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-4">
              <div>
                <Label htmlFor="address" className="text-xs font-semibold text-slate-700 mb-1.5 block">Street Address</Label>
                <Input
                  id="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    setAddress(value);
                    updateProfile({ address: value || null });
                  }}
                  placeholder="e.g. 123 Medical Center Blvd, Suite 400"
                  className="h-10 rounded-xl border-slate-200 bg-white text-sm shadow-2xs"
                />
              </div>

              <div className="grid gap-3.5 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">Country</Label>
                  <SingleSelectDropdown
                    options={allCountries}
                    value={selectedCountry?.isoCode}
                    placeholder="Select Country"
                    onSelect={async (val) => {
                      const c = allCountries.find(x => x.value === val);
                      setSelectedCountry(c);
                      setSelectedState(null);
                      setCountry(c?.name || "");
                      setState("");
                      setCity("");
                      await updateProfile({ country: c?.name || null, state: null, city: null });
                    }}
                    triggerClassName="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-left text-sm text-slate-900 shadow-2xs"
                    panelClassName="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-xl"
                    searchWrapperClassName="flex items-center border-b border-slate-200 p-2"
                    searchIconClassName="mr-2 h-4 w-4 text-slate-400"
                    searchInputClassName="w-full text-sm outline-none placeholder:text-slate-400"
                    listClassName="max-h-60 overflow-y-auto"
                    optionClassName="px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-600"
                    emptyStateClassName="px-3 py-2 text-sm text-slate-500"
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold text-slate-700 mb-1.5 block">State / Province</Label>
                  <SingleSelectDropdown
                    options={states}
                    disabled={!selectedCountry}
                    value={selectedState?.isoCode}
                    placeholder={selectedCountry ? "Select State" : "Select Country First"}
                    onSelect={async (val) => {
                      const s = states.find(x => x.value === val);
                      setSelectedState(s);
                      setState(s?.name || "");
                      setCity("");
                      await updateProfile({ state: s?.name || null, city: null });
                    }}
                    triggerClassName="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-left text-sm text-slate-900 shadow-2xs disabled:bg-slate-50 disabled:text-slate-400"
                    panelClassName="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-xl"
                    searchWrapperClassName="flex items-center border-b border-slate-200 p-2"
                    searchIconClassName="mr-2 h-4 w-4 text-slate-400"
                    searchInputClassName="w-full text-sm outline-none placeholder:text-slate-400"
                    listClassName="max-h-60 overflow-y-auto"
                    optionClassName="px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-600"
                    emptyStateClassName="px-3 py-2 text-sm text-slate-500"
                  />
                </div>

                <div>
                  <Label htmlFor="city" className="text-xs font-semibold text-slate-700 mb-1.5 block">City</Label>
                  {cities.length > 0 ? (
                    <SingleSelectDropdown
                      options={cities}
                      disabled={!selectedState}
                      value={city}
                      placeholder={selectedState ? "Select City" : "Select State First"}
                      onSelect={async (val) => {
                        setCity(val);
                        await updateProfile({ city: val });
                      }}
                      triggerClassName="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-left text-sm text-slate-900 shadow-2xs disabled:bg-slate-50 disabled:text-slate-400"
                      panelClassName="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-900 shadow-xl"
                      searchWrapperClassName="flex items-center border-b border-slate-200 p-2"
                      searchIconClassName="mr-2 h-4 w-4 text-slate-400"
                      searchInputClassName="w-full text-sm outline-none placeholder:text-slate-400"
                      listClassName="max-h-60 overflow-y-auto"
                      optionClassName="px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-600 data-[selected=true]:bg-blue-50 data-[selected=true]:text-blue-600"
                      emptyStateClassName="px-3 py-2 text-sm text-slate-500"
                    />
                  ) : (
                    <Input
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        setCity(value);
                        updateProfile({ city: value || null });
                      }}
                      placeholder="City"
                      className="h-10 rounded-xl border-slate-200 bg-white text-sm shadow-2xs"
                    />
                  )}
                </div>

                <div>
                  <Label htmlFor="zipCode" className="text-xs font-semibold text-slate-700 mb-1.5 block">Zip / Postal Code</Label>
                  <Input
                    id="zipCode"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      setZipCode(value);
                      updateProfile({ zipCode: value || null });
                    }}
                    placeholder="e.g. 10001"
                    className="h-10 rounded-xl border-slate-200 bg-white text-sm shadow-2xs"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ═════════════════════════════════════════════════════════════
            RIGHT COLUMN: PREFERENCES & SECURITY
        ═════════════════════════════════════════════════════════════ */}
        <div className="space-y-6">
          {/* ─────────────────────────────────────────────────────────────
              4. SOAP NOTE DISPLAY PREFERENCES
          ───────────────────────────────────────────────────────────── */}
          <Card className="relative z-20 focus-within:z-50 border border-slate-200/80 bg-white shadow-xs hover:shadow-sm transition-shadow rounded-2xl">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-600 border border-purple-100/80 shadow-2xs">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-slate-800">SOAP Note Display</CardTitle>
                  <CardDescription className="text-xs text-slate-500 mt-0.5">
                    Control how newly generated SOAP notes appear on your screen.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-5">
              {/* Typing animation setting */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 p-4 border border-slate-200/80 rounded-xl bg-slate-50/50">
                <div className="space-y-1 min-w-0">
                  <h2 className="text-sm font-semibold text-slate-800">
                    Typing Animation
                  </h2>
                  <p className="text-xs text-slate-500 leading-relaxed max-w-lg">
                    When enabled, Subjective, Objective, Assessment, and Plan reveal
                    with a typewriter effect after generation. Off by default — notes
                    appear instantly.
                  </p>
                </div>

                {/* Toggle */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span
                    className={`text-xs font-semibold min-w-[28px] px-2 py-0.5 rounded-full ${soapTypingAnimationEnabled
                      ? "bg-blue-50 text-blue-600 border border-blue-200/60"
                      : "bg-slate-100 text-slate-500 border border-slate-200/60"
                      }`}
                  >
                    {soapTypingAnimationEnabled ? "On" : "Off"}
                  </span>

                  <Switch
                    checked={soapTypingAnimationEnabled}
                    onCheckedChange={async (checked) => {
                      setSoapTypingAnimationEnabled(checked);
                      await updateProfile({
                        soapTypingAnimationEnabled: checked,
                      });
                      toast.success(
                        checked
                          ? "Typing animation enabled"
                          : "Typing animation disabled — notes will appear instantly"
                      );
                    }}
                  />
                </div>
              </div>

              {/* Interactive Preview */}
              <div className="rounded-xl border border-dashed border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Live Preview
                  </p>

                  {soapTypingAnimationEnabled && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2.5 py-1 rounded-lg transition-colors font-medium"
                      onClick={() => {
                        setTypingExampleText("");
                        const SAMPLE =
                          "Subjective: Patient reports mild headache for 2 days, no fever. Objective: Alert, vitals stable. Assessment: Tension headache. Plan: Hydration, follow up PRN.";
                        let i = 0;
                        const tick = setInterval(() => {
                          i = Math.min(SAMPLE.length, i + 2);
                          setTypingExampleText(SAMPLE.slice(0, i));
                          if (i >= SAMPLE.length) {
                            clearInterval(tick);
                          }
                        }, 28);
                      }}
                    >
                      ↻ Replay
                    </button>
                  )}
                </div>

                {/* Example text */}
                <p className="text-sm text-slate-700 font-mono leading-relaxed min-h-[4.5rem] whitespace-pre-wrap bg-slate-50/50 p-3 rounded-lg border border-slate-100">
                  {typingExampleText}
                  {soapTypingAnimationEnabled &&
                    typingExampleText.length <
                    "Subjective: Patient reports mild headache for 2 days, no fever. Objective: Alert, vitals stable. Assessment: Tension headache. Plan: Hydration, follow up PRN."
                      .length && (
                      <span className="inline-block w-0.5 h-4 ml-0.5 align-middle bg-teal-500 animate-pulse" />
                    )}
                </p>

                <p className="text-xs text-slate-400 mt-2">
                  {soapTypingAnimationEnabled
                    ? "This is how new SOAP notes will type out after generation."
                    : "With the toggle off, the full note appears at once (recommended)."}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* ─────────────────────────────────────────────────────────────
              5. SECURITY & AUTHENTICATION
          ───────────────────────────────────────────────────────────── */}
          <Card className="relative z-10 focus-within:z-50 border border-slate-200/80 bg-white shadow-xs hover:shadow-sm transition-shadow rounded-2xl">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100/80 shadow-2xs">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-lg font-bold text-slate-800">Security & Authentication</CardTitle>
                  <CardDescription className="text-xs text-slate-500 mt-0.5">
                    Manage your sign-in credentials and multi-factor authentication methods.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 space-y-6">
              {/* Email Address */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="account_display_email" className="text-xs font-semibold text-slate-700">Account Email</Label>
                  {emailVerified && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-full">
                      <CheckCircle2 className="h-3 w-3" /> Verified
                    </span>
                  )}
                </div>
                <div className="relative">
                  <Input
                    disabled
                    value={email}
                    name="account_display_email"
                    id="account_display_email"
                    autoComplete="off"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    className="h-10 rounded-xl bg-slate-50 border-slate-200 text-slate-600 text-sm cursor-not-allowed pr-9 shadow-2xs"
                  />
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                </div>
                <p className="text-xs text-slate-400">Your account email address is permanent and cannot be modified.</p>
              </div>

              <div className="h-px bg-slate-100" />

              {/* Password Management */}
              <form
                autoComplete="off"
                onSubmit={(e) => e.preventDefault()}
                className="space-y-4"
              >
                {/* Decoy fields to intercept aggressive browser credential autofill */}
                <input
                  type="text"
                  name="prevent_autofill_username"
                  tabIndex={-1}
                  aria-hidden="true"
                  autoComplete="off"
                  style={{ position: "absolute", opacity: 0, height: 0, width: 0, zIndex: -1, pointerEvents: "none" }}
                />
                <input
                  type="password"
                  name="prevent_autofill_password"
                  tabIndex={-1}
                  aria-hidden="true"
                  autoComplete="off"
                  style={{ position: "absolute", opacity: 0, height: 0, width: 0, zIndex: -1, pointerEvents: "none" }}
                />

                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    {hasPassword ? "Change Account Password" : "Set Account Password (Enable Hybrid Login)"}
                  </h3>
                  {!hasPassword && (
                    <p className="text-xs text-slate-500 mt-1 max-w-xl">
                      You are currently signing in exclusively via Google OAuth. To enable logging in with your password as well, set a new password below.
                    </p>
                  )}
                </div>

                <div className={`grid grid-cols-1 ${hasPassword ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-2.5`}>
                  {hasPassword && (
                    <div>
                      <Label htmlFor="current_password_manual" className="text-xs font-semibold text-slate-600 mb-1 block">Current Password</Label>
                      <Input
                        type="password"
                        name="current_password_manual"
                        id="current_password_manual"
                        autoComplete="new-password"
                        placeholder="Current password"
                        value={oldPassword}
                        readOnly={!unlockedInputs.oldPassword && !oldPassword}
                        onFocus={() => unlockInput("oldPassword")}
                        onChange={(e) => setOldPassword(e.target.value)}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-bwignore="true"
                        data-protonpass-ignore="true"
                        data-form-type="other"
                        aria-autocomplete="none"
                        className="h-10 rounded-xl border-slate-200 text-sm shadow-2xs"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="new_password_manual" className="text-xs font-semibold text-slate-600 mb-1 block">New Password</Label>
                    <Input
                      type="password"
                      name="new_password_manual"
                      id="new_password_manual"
                      autoComplete="new-password"
                      placeholder="New password"
                      value={newPassword}
                      readOnly={!unlockedInputs.newPassword && !newPassword}
                      onFocus={() => unlockInput("newPassword")}
                      onChange={(e) => setNewPassword(e.target.value)}
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-protonpass-ignore="true"
                      data-form-type="other"
                      aria-autocomplete="none"
                      className="h-10 rounded-xl border-slate-200 text-sm shadow-2xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_password_manual" className="text-xs font-semibold text-slate-600 mb-1 block">Confirm Password</Label>
                    <Input
                      type="password"
                      name="confirm_password_manual"
                      id="confirm_password_manual"
                      autoComplete="new-password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      readOnly={!unlockedInputs.confirmPassword && !confirmPassword}
                      onFocus={() => unlockInput("confirmPassword")}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-protonpass-ignore="true"
                      data-form-type="other"
                      aria-autocomplete="none"
                      className="h-10 rounded-xl border-slate-200 text-sm shadow-2xs"
                    />
                  </div>
                </div>

                {/* Strength meter and requirements checklist */}
                {newPassword && (
                  <div className="space-y-3 py-2.5 px-3.5 bg-slate-50 rounded-xl border border-slate-100">
                    <div className="flex gap-1.5 items-center">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${i <= passedCount ? strengthColor : "bg-slate-200"
                            }`}
                        />
                      ))}
                      <span
                        className={`text-xs font-semibold ml-1.5 whitespace-nowrap ${passedCount === 4
                          ? "text-emerald-600"
                          : passedCount >= 3
                            ? "text-yellow-600"
                            : passedCount >= 2
                              ? "text-orange-500"
                              : "text-red-500"
                          }`}
                      >
                        {strengthLabel}
                      </span>
                    </div>

                    {/* Criteria checklist */}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-1">
                      {criteria.map((c) => {
                        const ok = c.test(newPassword);
                        return (
                          <div key={c.label} className="flex items-center gap-1.5">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${ok ? "bg-emerald-500" : "bg-slate-200"
                                }`}
                            >
                              {ok ? (
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                                  <path
                                    d="M2 5l2.5 2.5L8 3"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              ) : (
                                <svg className="w-2.5 h-2.5 text-slate-400" fill="none" viewBox="0 0 10 10">
                                  <path
                                    d="M3 3l4 4M7 3L3 7"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              )}
                            </div>
                            <span className={`text-xs leading-tight ${ok ? "text-slate-700 font-medium" : "text-slate-400"}`}>
                              {c.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="button"
                    className="h-9 px-4 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-2xs transition-colors"
                    onClick={async () => {
                      setPwMsg(null);
                      if (!newPassword || newPassword !== confirmPassword) {
                        setPwMsg({ type: "error", text: "Please check that new passwords match" });
                        return;
                      }
                      if (passedCount !== 4) {
                        setPwMsg({ type: "error", text: "Please meet all password requirements" });
                        return;
                      }
                      if (hasPassword && !oldPassword) {
                        setPwMsg({ type: "error", text: "Current password is required" });
                        return;
                      }
                      try {
                        const resp = await changePasswordApi(hasPassword ? oldPassword : undefined, newPassword);
                        setPwMsg({ type: "success", text: resp?.__meta?.message || "Password updated successfully" });
                        setHasPassword(true);
                        setOldPassword("");
                        setNewPassword("");
                        setConfirmPassword("");
                        setUnlockedInputs({});
                      } catch (e) {
                        setPwMsg({ type: "error", text: e?.response?.data?.body?.message || "Password update failed" });
                      }
                    }}
                  >
                    {hasPassword ? "Update Password" : "Set Password"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9 px-3.5 text-xs font-medium border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl transition-colors"
                    onClick={() => {
                      setOldPassword("");
                      setNewPassword("");
                      setConfirmPassword("");
                      setUnlockedInputs({});
                      setPwMsg(null);
                    }}
                  >
                    Clear
                  </Button>
                </div>
                {pwMsg && (
                  <p className={`text-xs font-medium mt-2 ${pwMsg.type === 'error' ? 'text-red-600' : 'text-emerald-600'}`}>
                    {pwMsg.text}
                  </p>
                )}
              </form>

              <div className="h-px bg-slate-100" />

              {/* Two-Factor Authentication (2FA) */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Two-Factor Authentication (2FA)</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Add an extra layer of protection to prevent unauthorized access to your account.
                  </p>
                </div>

                {!hasPassword ? (
                  <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 flex items-start gap-3.5">
                    <span className="p-2 bg-blue-50 text-blue-600 rounded-xl border border-blue-100 shrink-0">
                      <ShieldCheck className="h-4 w-4" />
                    </span>
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-slate-800">Security managed by Google</h4>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Your account is authenticated via Google. Security and Multi-Factor Authentication are managed directly by Google.
                        If you want to configure custom Email MFA or Authenticator App verification on VirtualScribe, please set an account password above first.
                      </p>
                    </div>
                  </div>
                ) : totpStep === "idle" ? (
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-slate-600">Choose your preferred login verification method:</p>

                    <div className="grid grid-cols-1 gap-3">
                      {/* Method 1: Password Only */}
                      <div
                        className={`flex items-start justify-between p-4 border rounded-2xl cursor-pointer transition duration-150 ${!totpEnabled && !mfaEnabled
                          ? "border-blue-500 bg-blue-50/30 shadow-2xs"
                          : "border-slate-200 bg-white hover:bg-slate-50/70"
                          }`}
                        onClick={async () => {
                          if (totpEnabled || mfaEnabled) {
                            if (totpEnabled) {
                              setShowDisableConfirmModal(true);
                            } else {
                              await onToggleMfa(false);
                            }
                          }
                        }}
                      >
                        <div className="flex gap-3.5">
                          <div className="mt-0.5 flex-shrink-0">
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${!totpEnabled && !mfaEnabled ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-white"
                              }`}>
                              {!totpEnabled && !mfaEnabled && <span className="w-1.5 h-1.5 bg-white rounded-full" />}
                            </div>
                          </div>
                          <div className="space-y-0.5">
                            <h4 className="text-xs font-bold text-slate-800">
                              Basic Security (Password Only)
                            </h4>
                            <p className="text-xs text-slate-500 leading-relaxed max-w-lg">
                              Log in using only your email address and password. No second-factor verification code required.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Method 2: Email Verification Codes */}
                      <div
                        className={`flex items-start justify-between p-4 border rounded-2xl cursor-pointer transition duration-150 ${!totpEnabled && mfaEnabled
                          ? "border-blue-500 bg-blue-50/30 shadow-2xs"
                          : "border-slate-200 bg-white hover:bg-slate-50/70"
                          }`}
                        onClick={async () => {
                          if (totpEnabled) {
                            setShowSwitchConfirmModal(true);
                          } else if (!mfaEnabled) {
                            await onToggleMfa(true);
                          }
                        }}
                      >
                        <div className="flex gap-3.5">
                          <div className="mt-0.5 flex-shrink-0">
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${!totpEnabled && mfaEnabled ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-white"
                              }`}>
                              {!totpEnabled && mfaEnabled && <span className="w-1.5 h-1.5 bg-white rounded-full" />}
                            </div>
                          </div>
                          <div className="space-y-0.5">
                            <h4 className="text-xs font-bold text-slate-800">
                              Strong Security (Email Verification Code)
                            </h4>
                            <p className="text-xs text-slate-500 leading-relaxed max-w-lg">
                              Receive a secure 6-digit verification code to your registered email inbox on every sign-in.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Method 3: Authenticator App */}
                      <div
                        className={`flex items-start justify-between p-4 border rounded-2xl cursor-pointer transition duration-150 ${totpEnabled
                          ? "border-blue-500 bg-blue-50/30 shadow-2xs"
                          : "border-slate-200 bg-white hover:bg-slate-50/70"
                          }`}
                        onClick={async () => {
                          if (!totpEnabled) {
                            await handleInitiateTotp();
                          }
                        }}
                      >
                        <div className="flex gap-3.5">
                          <div className="mt-0.5 flex-shrink-0">
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${totpEnabled ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-white"
                              }`}>
                              {totpEnabled && <span className="w-1.5 h-1.5 bg-white rounded-full" />}
                            </div>
                          </div>
                          <div className="space-y-0.5">
                            <h4 className="text-xs font-bold text-slate-800">
                              Maximum Security (Authenticator App TOTP)
                            </h4>
                            <p className="text-xs text-slate-500 leading-relaxed max-w-lg">
                              Secure your account using time-based offline passcodes from Google Authenticator, Microsoft Authenticator, or 1Password.
                            </p>
                          </div>
                        </div>
                        {totpEnabled && (
                          <div className="flex-shrink-0 ml-4 flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 py-0 px-2.5 text-xs font-semibold border-slate-200 text-slate-700 hover:bg-slate-50 transition rounded-lg"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowRegenInput(true);
                              }}
                              disabled={totpLoading}
                            >
                              Recovery Codes
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 py-0 px-2.5 text-xs font-semibold border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 transition rounded-lg"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowDisableConfirmModal(true);
                              }}
                              disabled={totpLoading}
                            >
                              Disable
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : totpStep === "setup" && (
                  <div className="space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-2">
                      <h3 className="text-xs font-bold text-slate-800">Set Up Your Authenticator App</h3>
                      <ol className="list-decimal list-inside text-xs text-slate-600 space-y-1 leading-relaxed">
                        <li>Scan the QR code below using your authenticator app (Google Authenticator, Microsoft Authenticator, etc.).</li>
                        <li>If you cannot scan the QR code, manually enter the secret key shown below.</li>
                        <li>Enter the 6-digit code generated by your app in the box below to complete setup.</li>
                      </ol>
                    </div>

                    <div className="flex flex-col md:flex-row items-center gap-6 justify-center py-5 bg-slate-50/50 border border-dashed border-slate-200 rounded-2xl">
                      {totpQrUrl && (
                        <div className="flex flex-col items-center gap-2">
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(totpQrUrl)}`}
                            className="w-36 h-36 border border-slate-200 p-2 bg-white rounded-2xl shadow-2xs"
                            alt="Authenticator QR Code"
                          />
                          <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">Scan with App</span>
                        </div>
                      )}

                      <div className="space-y-3 max-w-xs text-center md:text-left">
                        <div>
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Account name</h4>
                          <p className="text-xs font-medium text-slate-800 truncate">{email}</p>
                        </div>
                        <div>
                          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Secret Key (Manual Entry)</h4>
                          <div className="flex items-center justify-center md:justify-start gap-2 mt-1">
                            <code className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg border border-slate-200 font-mono tracking-wider">
                              {totpSecret}
                            </code>
                            <button
                              type="button"
                              className="text-blue-600 hover:text-blue-700 transition p-1 hover:bg-slate-100 rounded-lg"
                              onClick={() => {
                                navigator.clipboard.writeText(totpSecret);
                                toast.success("Secret key copied!");
                              }}
                              title="Copy Secret Key"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H5.4m1.5 1.5h9.75c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V9m1.5 9h8.25" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-center gap-3 pt-1">
                      <Label htmlFor="totp-code" className="text-xs font-semibold text-slate-700">Enter Authenticator 6-Digit Code</Label>
                      <div className="flex items-center gap-3 w-full max-w-xs">
                        <Input
                          id="totp-code"
                          type="text"
                          inputMode="numeric"
                          maxLength="6"
                          value={totpCode}
                          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          placeholder="e.g. 123456"
                          className="h-11 w-full text-center text-lg font-bold font-mono tracking-widest bg-white border border-slate-200 focus:ring-2 focus:ring-blue-500 rounded-xl"
                        />
                      </div>
                      <div className="flex items-center gap-3 w-full max-w-xs mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1 h-10 border border-slate-200 rounded-xl transition text-xs font-medium"
                          onClick={() => {
                            setTotpStep("idle");
                            setTotpCode("");
                          }}
                          disabled={totpLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          className="flex-1 h-10 bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-md text-white text-xs font-semibold rounded-xl transition"
                          onClick={handleVerifyAndEnableTotp}
                          disabled={totpLoading || totpCode.length !== 6}
                        >
                          {totpLoading ? "Verifying..." : "Verify & Enable"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      )}

      {/* Disable 2FA Confirmation Modal */}
      {showDisableConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-white border border-slate-100 rounded-3xl p-6 shadow-2xl space-y-6 scale-in duration-200 transform">
            <div className="flex items-start gap-4">
              <span className="p-3 bg-red-50 text-red-600 rounded-2xl">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </span>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-slate-800">Disable Two-Factor Authentication?</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Are you absolutely sure you want to disable Authenticator App 2FA? This will significantly reduce your account security and expose you to credential exploits.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11 border-2 border-slate-200 hover:bg-slate-50 text-slate-600 font-medium rounded-xl transition"
                onClick={() => setShowDisableConfirmModal(false)}
                disabled={totpLoading}
              >
                Keep Secure
              </Button>
              <Button
                className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl shadow-sm transition"
                onClick={confirmDisableTotp}
                disabled={totpLoading}
              >
                {totpLoading ? "Disabling..." : "Disable 2FA"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Switch to Email MFA Confirmation Modal */}
      {showSwitchConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-white border border-slate-100 rounded-3xl p-6 shadow-2xl space-y-6 scale-in duration-200 transform">
            <div className="flex items-start gap-4">
              <span className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </span>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-slate-800">Switch to Email Codes?</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  Switching to Email Verification Codes will replace your offline Authenticator App protection. This lowers your security level since email inbox codes are more vulnerable to interception than offline TOTP.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11 border-2 border-slate-200 hover:bg-slate-50 text-slate-600 font-medium rounded-xl transition"
                onClick={() => setShowSwitchConfirmModal(false)}
                disabled={totpLoading}
              >
                Keep Maximum
              </Button>
              <Button
                className="flex-1 h-11 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-xl shadow-sm transition"
                onClick={confirmSwitchToEmail}
                disabled={totpLoading}
              >
                {totpLoading ? "Switching..." : "Yes, Switch Method"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Recovery Codes - TOTP Code Input */}
      {showRegenInput && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-md bg-white border border-slate-100 rounded-3xl p-6 shadow-2xl space-y-6">
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-slate-800">Regenerate Recovery Codes</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                Enter your current authenticator code to generate new recovery codes. This will invalidate all existing codes.
              </p>
            </div>
            <div>
              <Label htmlFor="regen-code" className="text-sm font-medium text-slate-700">Authenticator Code</Label>
              <Input
                id="regen-code"
                type="text"
                inputMode="numeric"
                maxLength="6"
                value={regenTotpCode}
                onChange={(e) => setRegenTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="mt-1 h-11 text-center text-lg font-bold font-mono tracking-widest bg-white border border-slate-200 focus:ring-2 focus:ring-blue-500 rounded-xl"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11 border-2 border-slate-200 hover:bg-slate-50 text-slate-600 font-medium rounded-xl transition"
                onClick={() => { setShowRegenInput(false); setRegenTotpCode(""); }}
                disabled={totpLoading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-11 bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white font-medium rounded-xl transition"
                disabled={totpLoading || regenTotpCode.length !== 6}
                onClick={async () => {
                  setTotpLoading(true);
                  try {
                    const res = await regenerateRecoveryCodesApi(regenTotpCode.trim());
                    const data = res.data || res;
                    const codes = data.recoveryCodes || data?.data?.recoveryCodes;
                    if (codes && codes.length > 0) {
                      setRecoveryCodes(codes);
                      setShowRegenInput(false);
                      setRegenTotpCode("");
                      setShowRecoveryCodesModal(true);
                      toast.success("Recovery codes regenerated!");
                    } else {
                      toast.error("Failed to regenerate recovery codes.");
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.body?.message || "Invalid code. Please try again.");
                  } finally {
                    setTotpLoading(false);
                  }
                }}
              >
                {totpLoading ? "Generating..." : "Generate New Codes"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Recovery Codes Display Modal */}
      {showRecoveryCodesModal && recoveryCodes.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-lg bg-white border border-slate-100 rounded-3xl p-6 shadow-2xl space-y-5">
            <div className="flex items-start gap-4">
              <span className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                </svg>
              </span>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-slate-800">Save Your Recovery Codes</h3>
                <p className="text-sm text-slate-500 leading-relaxed">
                  These codes can be used to sign in if you lose access to your authenticator app. Each code can only be used <strong>once</strong>. Store them somewhere safe.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">
              <div className="grid grid-cols-2 gap-2">
                {recoveryCodes.map((code, i) => (
                  <div key={i} className="flex items-center justify-center px-3 py-2 bg-white border border-slate-200 rounded-lg">
                    <code className="text-sm font-bold font-mono text-slate-700 tracking-wider">{code}</code>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-xs text-red-700 font-medium flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 flex-shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                You won't be able to see these codes again. Save them now!
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                className="flex-1 h-11 border-2 border-slate-200 hover:bg-slate-50 text-slate-600 font-medium rounded-xl transition text-sm"
                onClick={() => {
                  navigator.clipboard.writeText(recoveryCodes.join("\n"));
                  toast.success("Recovery codes copied to clipboard!");
                }}
              >
                Copy All
              </Button>
              <Button
                variant="outline"
                className="flex-1 h-11 border-2 border-slate-200 hover:bg-slate-50 text-slate-600 font-medium rounded-xl transition text-sm"
                onClick={() => {
                  const text = `VirtualScribe Recovery Codes\n${'='.repeat(30)}\n\n${recoveryCodes.map((c, i) => `${i + 1}. ${c}`).join('\n')}\n\nGenerated: ${new Date().toLocaleString()}\nEach code can only be used once.`;
                  const blob = new Blob([text], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "virtualscribe-recovery-codes.txt";
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Recovery codes downloaded!");
                }}
              >
                Download .txt
              </Button>
            </div>

            <Button
              className="w-full h-11 bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white font-medium rounded-xl transition"
              onClick={() => {
                setShowRecoveryCodesModal(false);
                setRecoveryCodes([]);
              }}
            >
              I've Saved My Codes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}